import { Offer } from '~app/modules/product/models';
import { ProductIndexBasePrice } from '~app/modules/product/models/product-index-base-price.model';
import { ProductIndexPrice } from '~app/modules/product/models/product-index-price.model';
import { ProductIndexRegionInfo } from '~app/modules/product/models/product-index-region-info.model';
import { ProductIndexSavePrice } from '~app/modules/product/models/product-index-save-price.model';
import { ProductIndexShipping } from '~app/modules/product/models/product-index-shipping.model';
import { ProductIndexUnitPrice } from '~app/modules/product/models/product-index-unit-price.model';
import { VolumePrice } from '~app/modules/product/models/volume-price.model';
import { PriceTypes } from '~shared/modules/price-type-toggler/models/price-types.enum';

export namespace VatDeciderUtilBuyAgain {
  export function applyVatOnOffer(offer: Offer, priceType: string) {
    const isOriginPriceDisplayed = offer?.originRegionId !== offer?.destinationRegionId
      && priceType === PriceTypes.b2b;

    const selectedOffer = { ...offer };

    if (isOriginPriceDisplayed) {
      selectedOffer.basePrice = selectedOffer.basePriceOrigin;
      selectedOffer.pricePerUnit = selectedOffer.pricePerUnitOrigin;
      selectedOffer.price = selectedOffer.priceOrigin;
      selectedOffer.volumePrice = selectedOffer.volumePriceOrigin;
      return selectedOffer;
    }

    return selectedOffer;
  }

  export function shouldApplyZeroVAT(offer: Offer, priceType: string): boolean {
    const isB2B = priceType === PriceTypes.b2b;
    return isCrossBorderOffer(offer) && isB2B;
  }

  export function getVatRegionInfo(offer: Offer, priceType: string): ProductIndexRegionInfo {
    if (shouldApplyZeroVAT(offer, priceType)) {
      return getRegionInfoWithZeroVAT(offer.destinationRegionInfo);
    }

    if (!isCrossBorderOffer(offer)) {
      return offer?.originRegionInfo;
    }

    return offer.destinationRegionInfo;
  }

  export function isCrossBorderOffer(offer: Offer): boolean {
    return offer?.originRegionId !== offer?.destinationRegionId;
  }

  function setBasePriceZeroVAT(basePrice: ProductIndexBasePrice): ProductIndexBasePrice {
    if (!basePrice) {
      return null;
    }

    return {
      ...basePrice,
      price: {
        ...basePrice.price,
        gross: basePrice.price.net
      }
    };
  }

  function setUnitPriceZeroVAT(unitPrice: ProductIndexUnitPrice): ProductIndexUnitPrice {
    if (!unitPrice) {
      return null;
    }

    return {
      ...unitPrice,
      price: {
        ...unitPrice.price,
        gross: unitPrice.price.net
      }
    };
  }

  function setReferencePriceZeroVAT(referencePrice: ProductIndexPrice): ProductIndexPrice {
    if (!referencePrice) {
      return null;
    }

    return {
      ...referencePrice,
      gross: referencePrice.net
    };
  }

  function setMRSPPriceZeroVAT(msrpPrice: ProductIndexPrice): ProductIndexPrice {
    if (!msrpPrice) {
      return null;
    }

    return {
      ...msrpPrice,
      gross: msrpPrice.net
    };
  }

  function setPriceZeroVAT(price: ProductIndexPrice): ProductIndexPrice {
    if (!price) {
      return null;
    }

    return {
      ...price,
      gross: price.net
    };
  }

  function setSavePriceZeroVAT(savePrice: ProductIndexSavePrice): ProductIndexSavePrice {
    if (!savePrice) {
      return null;
    }

    return {
      ...savePrice,
      price: {
        ...savePrice.price,
        gross: savePrice.price.net
      }
    };
  }

  function setShippingPriceZeroVAT(shipping: ProductIndexShipping): ProductIndexShipping {
    if (!shipping || !shipping.price) {
      return null;
    }

    return {
      ...shipping,
      price: {
        ...shipping.price,
        gross: shipping.price.net
      }
    };
  }

  export function getRegionInfoWithZeroVAT(productIndexRegionInfo: ProductIndexRegionInfo): ProductIndexRegionInfo {
    // TODO: Review properites and apply Zero VAT for volume price
    let volumePrices;

    const basePrice = setBasePriceZeroVAT(productIndexRegionInfo.basePrice);
    const referencePrice = setReferencePriceZeroVAT(productIndexRegionInfo.referencePrice);
    const unitPrice = setUnitPriceZeroVAT(productIndexRegionInfo.unitPrice);
    const price = setPriceZeroVAT(productIndexRegionInfo.price);
    const msrpPrice = setMRSPPriceZeroVAT(productIndexRegionInfo.msrpPrice);

    if (productIndexRegionInfo?.volumePrices?.length) {
      volumePrices = productIndexRegionInfo?.volumePrices.map(
        (volumePriceItem: VolumePrice) => getVolumePriceItemWithZeroVAT(volumePriceItem)
      );
    }

    return {
      ...productIndexRegionInfo,
      ...(volumePrices && { volumePrices }),
      ...(price && { price }),
      ...(basePrice && { basePrice }),
      ...(msrpPrice && { msrpPrice }),
      ...(referencePrice && { referencePrice }),
      ...(unitPrice && { unitPrice })
    };
  }

  function getVolumePriceItemWithZeroVAT(volumePriceItem: VolumePrice): VolumePrice {
    const price: ProductIndexPrice = setPriceZeroVAT(volumePriceItem.price);
    const basePrice: ProductIndexBasePrice = setBasePriceZeroVAT(volumePriceItem.basePrice);
    const unitPrice: ProductIndexUnitPrice = setUnitPriceZeroVAT(volumePriceItem.unitPrice);
    const savePrice: ProductIndexSavePrice = setSavePriceZeroVAT(volumePriceItem.savePrice);

    return {
      ...volumePriceItem,
      ...(price && { price }),
      ...(basePrice && { basePrice }),
      ...(unitPrice && { unitPrice }),
      ...(savePrice && { savePrice })
    };
  }
}
